import { Link, useNavigate } from '@tanstack/react-router'
import { Building2, Plus, Search } from 'lucide-react'
import { useId, useState } from 'react'
import { EmptyState } from '@/components/ui/EmptyState'
import { ErrorState } from '@/components/ui/ErrorState'
import { Modal } from '@/components/ui/Modal'
import { PageHeader } from '@/components/ui/PageHeader'
import { Select } from '@/components/ui/Select'
import { Skeleton } from '@/components/ui/Skeleton'
import { useIsAdmin } from '@/lib/auth'
import { brandRank } from '@/lib/brand'
import { useClientList, useCreateClient } from '@/lib/clients-api'
import { toDecimal, toNumber } from '@/lib/decimal'
import { useBrandTemplates } from '@/lib/settings-api'
import type { ClientListItem, CreateClientInput, InvoiceGrouping, RateDisplay } from '@/lib/clients-types'
import { cn } from '@/lib/utils'

const GROUPING_OPTIONS: Array<{ value: InvoiceGrouping; label: string }> = [
  { value: 'BY_CATEGORY', label: 'Per categorie' },
  { value: 'BY_PERSON', label: 'Per persoon' },
  { value: 'BY_PROJECT', label: 'Per project' },
  { value: 'BY_CATEGORY_LINES', label: 'Categorie als factuurregel' },
  { value: 'SINGLE_GROUP', label: 'Eén groep' },
]
const RATE_OPTIONS: Array<{ value: RateDisplay; label: string }> = [
  { value: 'PER_LINE', label: 'Per regel' },
  { value: 'ONCE', label: 'Eenmalig' },
  { value: 'HIDDEN', label: 'Verborgen' },
]

export function ClientsPage() {
  const isAdmin = useIsAdmin()
  const [search, setSearch] = useState('')
  const [createOpen, setCreateOpen] = useState(false)
  const listQuery = useClientList(search.trim() || undefined)
  const clients = listQuery.data ?? []
  const principals = clients.filter((client) => !client.internal)
  const internalHolders = clients.filter((client) => client.internal)

  return (
    <div className="space-y-6">
      <PageHeader
        title="Klanten"
        description="Tarieven, verdeelsleutels, projecten en categorieen per klant."
        actions={
          isAdmin ? (
            <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>
              <Plus className="h-4 w-4" />
              Nieuwe klant
            </button>
          ) : null
        }
      />

      <div className="flex h-9 w-full max-w-sm items-center gap-2 rounded-lg border border-hairline bg-surface px-2.5 text-sm text-ink-faint">
        <Search className="h-4 w-4" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Zoek klant"
          className="flex-1 bg-transparent text-ink outline-hidden placeholder:text-ink-faint"
        />
      </div>

      {listQuery.isError ? (
        <ErrorState error={listQuery.error} onRetry={() => listQuery.refetch()} />
      ) : listQuery.isLoading ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[72px] w-full rounded-card" />
          ))}
        </div>
      ) : clients.length === 0 ? (
        <EmptyState icon={Building2} title="Geen klanten gevonden" description="Pas de zoekopdracht aan of voeg een nieuwe klant toe." />
      ) : (
        <div className="space-y-6">
          {principals.length > 0 ? (
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
              {principals.map((client) => (
                <ClientCard key={client.id} client={client} />
              ))}
            </div>
          ) : null}
          {internalHolders.length > 0 ? (
            <section aria-labelledby="huisstijl-intern">
              <p id="huisstijl-intern" className="label-caps mb-2">Intern</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
                {internalHolders.map((client) => (
                  <ClientCard key={client.id} client={client} />
                ))}
              </div>
            </section>
          ) : null}
        </div>
      )}

      {createOpen ? <CreateClientModal onClose={() => setCreateOpen(false)} /> : null}
    </div>
  )
}

/**
 * Een interne houder draagt geen merkkleur: die hoort bij een opdrachtgever, en
 * dit is er geen. De kop boven het blok zegt al wat het is, dus de kaart hoeft
 * het niet nog eens te herhalen.
 */
function ClientCard({ client }: { client: ClientListItem }) {
  const projects = `${client.projectCount} project${client.projectCount === 1 ? '' : 'en'}`
  const subtitle = [client.city, projects].filter(Boolean).join(' · ')

  return (
    <Link
      to="/klanten/$id"
      params={{ id: client.id }}
      className="group flex items-center gap-3 rounded-card border border-hairline bg-surface p-4 shadow-card transition-all duration-150 ease-spring hover:-translate-y-0.5 hover:border-accent-500/30"
    >
      <div
        aria-hidden="true"
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-sm font-semibold',
          client.internal ? 'bg-fill-1 text-ink-faint' : 'text-white',
        )}
        style={client.internal ? undefined : { backgroundColor: client.brandColor }}
      >
        {client.companyName.slice(0, 2).toUpperCase()}
      </div>
      <div className="min-w-0">
        <p className="truncate font-medium text-ink">{client.companyName}</p>
        <p className="truncate text-sm text-ink-faint">{subtitle}</p>
      </div>
    </Link>
  )
}

function CreateClientModal({ onClose }: { onClose: () => void }) {
  const templates = useBrandTemplates()
  const brandOptions = [...(templates.data ?? [])]
    .sort((a, b) => brandRank(a.code) - brandRank(b.code))
    .map((template) => ({ value: template.code, label: template.displayName }))
  const brandLabelId = useId()
  const groupingLabelId = useId()
  const rateLabelId = useId()
  const navigate = useNavigate()
  const createClient = useCreateClient()
  const [companyName, setCompanyName] = useState('')
  const [brandCode, setBrandCode] = useState('gentle_innovations')
  const [invoiceEmail, setInvoiceEmail] = useState('')
  const [street, setStreet] = useState('')
  const [postalCode, setPostalCode] = useState('')
  const [city, setCity] = useState('')
  const [country, setCountry] = useState('NL')
  const [vatPercentage, setVatPercentage] = useState('21,00')
  const [vatReversed, setVatReversed] = useState(false)
  const [groupingStrategy, setGroupingStrategy] = useState<InvoiceGrouping>('BY_CATEGORY')
  const [rateDisplay, setRateDisplay] = useState<RateDisplay>('ONCE')

  const canSave = companyName.trim().length > 0 && brandCode.length > 0

  function save() {
    if (!canSave) return
    const input: CreateClientInput = {
      companyName: companyName.trim(),
      brandCode,
      invoiceEmail: invoiceEmail.trim() || null,
      street: street.trim(),
      postalCode: postalCode.trim(),
      city: city.trim(),
      country: country.trim() || 'NL',
      vatPercentage: toDecimal(toNumber(vatPercentage.replace(',', '.'))),
      vatReversed,
      groupingStrategy,
      rateDisplay,
    }
    createClient.mutate(input, {
      onSuccess: (detail) => {
        onClose()
        navigate({ to: '/klanten/$id', params: { id: detail.id } })
      },
    })
  }

  return (
    <Modal
      open
      onOpenChange={(o) => !o && onClose()}
      title="Nieuwe klant"
      description="Vul de basisgegevens in; tarieven en verdeling stel je daarna in op de klantpagina."
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose}>Annuleren</button>
          <button type="button" className="btn-primary" onClick={save} disabled={!canSave || createClient.isPending}>
            {createClient.isPending ? 'Aanmaken…' : 'Aanmaken'}
          </button>
        </>
      }
    >
      <div className="space-y-3">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Bedrijfsnaam</span>
          <input value={companyName} onChange={(e) => setCompanyName(e.target.value)} className="field" placeholder="Bijv. Zig Software B.V." />
        </label>
        <label className="block">
          <span id={brandLabelId} className="mb-1.5 block text-sm font-medium text-ink-soft">Huisstijl</span>
          <Select
            value={brandCode}
            onValueChange={setBrandCode}
            options={brandOptions}
            ariaLabelledby={brandLabelId}
            className="w-full"
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Factuur-e-mail</span>
          <input value={invoiceEmail} onChange={(e) => setInvoiceEmail(e.target.value)} type="email" className="field" placeholder="facturen@klant.nl" />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Adres</span>
          <input value={street} onChange={(e) => setStreet(e.target.value)} className="field" placeholder="Straat en nummer" />
        </label>
        <div className="grid grid-cols-[minmax(0,120px)_minmax(0,1fr)] gap-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Postcode</span>
            <input value={postalCode} onChange={(e) => setPostalCode(e.target.value)} className="field" placeholder="1234 AB" />
          </label>
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">Plaats</span>
            <input value={city} onChange={(e) => setCity(e.target.value)} className="field" placeholder="Amsterdam" />
          </label>
        </div>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Land</span>
          <input value={country} onChange={(e) => setCountry(e.target.value)} className="field" placeholder="NL" />
        </label>
        <div className="grid grid-cols-2 items-end gap-2">
          <label className="block">
            <span className="mb-1.5 block text-sm font-medium text-ink-soft">BTW %</span>
            <input value={vatPercentage} onChange={(e) => setVatPercentage(e.target.value)} inputMode="decimal" className="field num text-right" placeholder="21,00" />
          </label>
          <label className="flex h-9 cursor-pointer items-center gap-1.5 text-sm text-ink-soft">
            <input type="checkbox" checked={vatReversed} onChange={(e) => setVatReversed(e.target.checked)} className="h-3.5 w-3.5 accent-accent-600" />
            BTW verlegd
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <label className="block">
            <span id={groupingLabelId} className="mb-1.5 block text-sm font-medium text-ink-soft">Groepering</span>
            <Select
              value={groupingStrategy}
              onValueChange={(v) => setGroupingStrategy(v as InvoiceGrouping)}
              options={GROUPING_OPTIONS.map((g) => ({ value: g.value, label: g.label }))}
              ariaLabelledby={groupingLabelId}
              className="w-full"
            />
          </label>
          <label className="block">
            <span id={rateLabelId} className="mb-1.5 block text-sm font-medium text-ink-soft">Tariefweergave</span>
            <Select
              value={rateDisplay}
              onValueChange={(v) => setRateDisplay(v as RateDisplay)}
              options={RATE_OPTIONS.map((r) => ({ value: r.value, label: r.label }))}
              ariaLabelledby={rateLabelId}
              className="w-full"
            />
          </label>
        </div>
        <p className="rounded-lg bg-fill-1 px-3 py-2 text-xs text-ink-faint">
          Rapportage-ontvangers stel je na het aanmaken in op de pagina Rapportages.
        </p>
      </div>
    </Modal>
  )
}
